import { ApproverQuestion, ApproverQuestionAnswerRecord } from '../utils/approverQuestions';

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'analista' | 'usuario' | 'gerente';
  is_active: boolean;
  can_approve?: boolean;
  can_be_requester_approver?: boolean;
  can_access_purchase_tabs?: boolean;
  can_access_projects?: boolean;
  projects_access_level?: string | null;
  can_view_all_documents?: boolean;
  can_manage_changes?: boolean;
  can_edit_own_change_cab_deck?: boolean;
  can_edit_group_peer_change_cab_deck?: boolean;
  can_view_change_security_audit?: boolean;
  can_create_users?: boolean;
  can_manage_user_roles?: boolean;
  can_reset_user_passwords?: boolean;
  can_deactivate_users?: boolean;
  can_manage_user_admin_flags?: boolean;
  can_view_user_audit?: boolean;
  can_grant_any_user_admin_flags?: boolean;
  preferred_theme?: string;
  dashboard_group_filter?: number;
  include_third_party_tickets?: boolean;
  email_notify_ticket_opened?: boolean;
  email_notify_ticket_comment?: boolean;
  email_notify_ticket_closed?: boolean;
  must_change_password?: boolean;
  created_at: string;
}

export interface UserListResponse {
  items: User[];
  total: number;
  skip: number;
  limit: number;
}

export interface UserExportItem {
  email: string;
  name: string;
  role: 'admin' | 'analista' | 'usuario' | 'gerente';
  is_active: boolean;
  can_approve: boolean;
  can_be_requester_approver: boolean;
  can_access_purchase_tabs: boolean;
  can_access_projects?: boolean;
  projects_access_level?: string | null;
  can_view_all_documents: boolean;
  can_manage_changes?: boolean;
  can_edit_own_change_cab_deck?: boolean;
  can_edit_group_peer_change_cab_deck?: boolean;
  can_view_change_security_audit?: boolean;
}

export interface UsersExportPayload {
  version: string;
  exported_at: string;
  users: UserExportItem[];
}

export interface UserImportItem {
  email: string;
  name: string;
  role: 'admin' | 'analista' | 'usuario' | 'gerente';
  is_active: boolean;
  can_approve: boolean;
  can_be_requester_approver: boolean;
  can_access_purchase_tabs: boolean;
  can_access_projects?: boolean;
  projects_access_level?: string | null;
  can_view_all_documents: boolean;
  password?: string;
}

export interface UsersImportPayload {
  version: string;
  users: UserImportItem[];
}

export interface UsersImportResultItem {
  email: string;
  action: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
}

export interface UsersImportResponse {
  success: boolean;
  dry_run: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  results: UsersImportResultItem[];
}

export interface Group {
  id: number;
  name: string;
  description?: string;
  email?: string;
  is_active: boolean;
  /** false = grupo legado/sistema/integrado; não pode ser excluído pela UI */
  is_user_created?: boolean;
  created_at: string;
  members?: GroupMember[];
}

export interface GroupMember {
  id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  is_leader: boolean;
}

export interface GroupExportMember {
  user_email?: string;
  user_name?: string;
  is_leader: boolean;
}

export interface GroupExportItem {
  name: string;
  description?: string;
  email?: string;
  is_active: boolean;
  members: GroupExportMember[];
}

export interface GroupsExportPayload {
  version: string;
  exported_at: string;
  groups: GroupExportItem[];
}

export interface GroupImportMember {
  user_email: string;
  is_leader: boolean;
}

export interface GroupImportItem {
  name: string;
  description?: string;
  email?: string;
  is_active: boolean;
  members: GroupImportMember[];
}

export interface GroupsImportPayload {
  version: string;
  groups: GroupImportItem[];
}

export interface GroupsImportResultItem {
  name: string;
  action: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
}

export interface GroupsImportResponse {
  success: boolean;
  dry_run: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  results: GroupsImportResultItem[];
}

export interface Supplier {
  id: number;
  corporate_name: string;
  trade_name?: string;
  contact_name: string;
  email: string;
  phone?: string;
  cnpj?: string | null;
  state_registration?: string;
  municipal_registration?: string;
  legal_nature?: string;
  opening_date?: string;
  registration_status?: string;
  system_registration_date?: string;
  notes?: string;
  is_checklist: boolean;
  is_si_high: boolean;
  is_international?: boolean;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface SupplierListResponse {
  items: Supplier[];
  total: number;
  skip: number;
  limit: number;
}

export type AreaType = 'tickets' | 'purchases' | 'projects' | 'changes';

/** Resposta de /api/projects/issues */
export interface JiraProjectIssueRow {
  key: string;
  summary: string;
  project_key: string;
  project_name: string;
  issue_type: string;
  status: string;
  duedate?: string | null;
  created?: string | null;
  updated?: string | null;
  go_live_planned?: string | null;
  go_live_real?: string | null;
  hh_vendido_seconds?: number | null;
  hh_real_seconds?: number | null;
  jira_browse_url: string;
  extra_fields?: Record<string, unknown>;
  /** Preenchidos pelo cálculo de custos (decoração FE). */
  own_hours?: number;
  third_party_hours?: number;
  own_cost_cents?: number;
  third_party_cost_cents?: number;
  cost_cents?: number;
}

export interface ProjectsJiraSearchResponse {
  start_at: number;
  max_results: number;
  total?: number | null;
  issues: JiraProjectIssueRow[];
  jira_configured: boolean;
  applied_jql?: string | null;
  has_more?: boolean;
  next_cursor?: string | null;
}

export interface ProjectsJiraColumnOption {
  id: string;
  label: string;
  field_type?: string | null;
  is_custom: boolean;
}

export interface ProjectsIssueObservationResponse {
  issue_key: string;
  observation?: string | null;
  field_id: string;
}

export interface ProjectGlobalView {
  id: number;
  name: string;
  context: string;
  jql: string;
  selected_columns: string[];
  kpi_configs: Array<{ id: string; title?: string }>;
  column_widths: Record<string, number>;
  is_preset: boolean;
  preset_spec_id?: string | null;
  carteira_group?: 'operacional' | 'sem_receita' | 'interno' | null;
  access_level?: 'np0' | 'np3' | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectGlobalViewsExportPayload {
  schema_version: number;
  exported_at: string;
  views: Array<{
    name: string;
    context: string;
    jql: string;
    selected_columns: string[];
    kpi_configs: Array<{ id: string; title?: string }>;
    column_widths: Record<string, number>;
    is_preset: boolean;
    preset_spec_id?: string | null;
    carteira_group?: 'operacional' | 'sem_receita' | 'interno' | null;
    access_level?: 'np0' | 'np3' | null;
  }>;
}

export interface ProjectGlobalViewsImportPayload {
  schema_version: number;
  views: ProjectGlobalViewsExportPayload['views'];
}

export interface ProjectGlobalViewsImportResponse {
  schema_version: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface PmoCarteiraTotalItem {
  carteira_id: string;
  carteira_label: string;
  total_issues: number;
  hh_vendido_hours: number;
  hh_real_hours: number;
}

export interface PmoCarteiraTotalsResponse {
  items: PmoCarteiraTotalItem[];
  geral: PmoCarteiraTotalItem;
}

export interface ProjectCostCompany {
  id: number;
  name: string;
  description?: string | null;
  default_hourly_rate?: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SupplierUpsertPayload {
  corporate_name: string;
  contact_name: string;
  email: string;
  phone?: string;
  cnpj?: string;
  trade_name?: string;
  state_registration?: string;
  municipal_registration?: string;
  legal_nature?: string;
  opening_date?: string;
  registration_status?: string;
  system_registration_date?: string;
  notes?: string;
  is_checklist?: boolean;
  is_si_high?: boolean;
  is_international?: boolean;
}

export interface SupplierQuickCreatePayload {
  corporate_name: string;
  contact_name: string;
  email: string;
  phone?: string;
  cnpj?: string;
  is_checklist?: boolean;
  is_si_high?: boolean;
  is_international?: boolean;
}

export interface SupplierBatchImportErrorItem {
  row: number;
  corporate_name?: string;
  message: string;
}

export interface SupplierBatchImportResponse {
  success: boolean;
  total_rows: number;
  created: number;
  skipped: number;
  errors: number;
  error_details: SupplierBatchImportErrorItem[];
}

export interface SupplierManualEntry {
  corporate_name: string;
  contact_name: string;
  email: string;
  phone?: string;
  cnpj?: string;
  created_supplier_id?: number;
}

export interface SupplierSelectionValue {
  version: number;
  selected_supplier_ids: number[];
  manual_suppliers: SupplierManualEntry[];
}

export interface ProjectCostRole {
  id: number;
  name: string;
  description?: string | null;
  default_hourly_rate?: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectCostCompanyRoleRate {
  id: number;
  company_id: number;
  role_id: number;
  hourly_rate: number;
  effective_start?: string | null;
  effective_end?: string | null;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  company?: ProjectCostCompany | null;
  role?: ProjectCostRole | null;
}

export interface ProjectCostCollaborator {
  id: number;
  name: string;
  email?: string | null;
  jira_account_id?: string | null;
  jira_display_name?: string | null;
  user_id?: number | null;
  company_id: number;
  role_id?: number | null;
  hourly_rate_override?: number | null;
  is_third_party: boolean;
  is_active: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  company?: ProjectCostCompany | null;
  role?: ProjectCostRole | null;
}

export interface ProjectCostCollaboratorRate {
  id: number;
  collaborator_id: number;
  hourly_rate: number;
  effective_start?: string | null;
  effective_end?: string | null;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  collaborator?: ProjectCostCollaborator | null;
}

export interface ProjectCostDemandRate {
  id: number;
  demand_type: string;
  label: string;
  hourly_rate: number;
  effective_start?: string | null;
  effective_end?: string | null;
  is_active: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectCostCompanySummary {
  company_id?: number | null;
  company_name: string;
  hours: number;
  cost_cents: number;
}

export interface ProjectCostCollaboratorSummary {
  collaborator_id?: number | null;
  collaborator_name: string;
  company_name: string;
  role_name?: string | null;
  hours: number;
  effective_hourly_rate_cents: number;
  effective_hourly_rate_source: string;
  effective_hourly_rate_source_label: string;
  cost_cents: number;
}

export interface ProjectCostIssueSummary {
  issue_key: string;
  project_key: string;
  issue_summary: string;
  issue_status: string;
  issue_observation: string;
  has_custom_observation: boolean;
  demand_type: string;
  hours: number;
  own_hours: number;
  third_party_hours: number;
  own_cost_cents: number;
  third_party_cost_cents: number;
  cost_cents: number;
  revenue_cents: number;
  parent_issue_key?: string | null;
}

export interface ProjectCostIssueObservationUpsertResponse {
  issue_key: string;
  observation?: string | null;
  has_custom_observation: boolean;
}

export interface ProjectCostCollaboratorImportErrorItem {
  row_number: number;
  message: string;
  name?: string | null;
  email?: string | null;
  jira_account_id?: string | null;
}

export interface ProjectCostCollaboratorImportResponse {
  total_rows: number;
  created: number;
  updated: number;
  dry_run: boolean;
  errors: ProjectCostCollaboratorImportErrorItem[];
}

export interface ProjectCostCollaboratorImportJiraUsersRequest {
  dry_run?: boolean;
  limit?: number;
  default_company_id?: number | null;
  default_role_id?: number | null;
  link_users?: boolean;
  is_third_party?: boolean;
  users?: ProjectCostCollaboratorImportJiraUserSelectionItem[];
}

export interface ProjectCostCollaboratorImportJiraUserSelectionItem {
  row_number?: number | null;
  selected: boolean;
  account_id?: string | null;
  display_name?: string | null;
  email?: string | null;
  company_id?: number | null;
  role_id?: number | null;
  is_third_party?: boolean | null;
}

export interface ProjectCostCollaboratorImportJiraUserItem {
  row_number: number;
  account_id?: string | null;
  display_name: string;
  email?: string | null;
  selected: boolean;
  already_exists: boolean;
  existing_collaborator_id?: number | null;
  company_id?: number | null;
  role_id?: number | null;
  linked_user_id?: number | null;
  is_third_party: boolean;
  message?: string | null;
}

export interface ProjectCostCollaboratorImportJiraUsersResponse {
  total_users: number;
  created: number;
  skipped: number;
  dry_run: boolean;
  preview_only: boolean;
  items: ProjectCostCollaboratorImportJiraUserItem[];
  errors: ProjectCostCollaboratorImportErrorItem[];
}

export interface ProjectCostCollaboratorResolveJiraIdsRequest {
  dry_run?: boolean;
  limit?: number;
  only_missing_account_id?: boolean;
}

export interface ProjectCostCollaboratorResolveJiraIdsItem {
  collaborator_id: number;
  collaborator_name: string;
  previous_jira_account_id?: string | null;
  resolved_jira_account_id?: string | null;
  resolved_jira_display_name?: string | null;
  resolved_jira_email?: string | null;
  matched_by?: string | null;
  confidence?: number | null;
  status: string;
  reason?: string | null;
}

export interface ProjectCostCollaboratorResolveJiraIdsResponse {
  dry_run: boolean;
  total_candidates: number;
  resolved: number;
  updated: number;
  skipped: number;
  items: ProjectCostCollaboratorResolveJiraIdsItem[];
}

export interface ProjectCostJiraWorklogAuthorItem {
  jira_account_id?: string | null;
  jira_display_name: string;
  jira_email?: string | null;
  worklog_entries: number;
  hours: number;
}

export interface ProjectCostJiraWorklogAuthorsResponse {
  year: number;
  applied_jql: string;
  total_authors: number;
  authors: ProjectCostJiraWorklogAuthorItem[];
}

export interface ProjectCostJiraUserSearchItem {
  account_id: string;
  display_name: string;
  email?: string | null;
  active: boolean;
}

export interface ProjectCostJiraUserSearchResponse {
  query: string;
  total: number;
  items: ProjectCostJiraUserSearchItem[];
}

export interface ProjectCostUnmatchedSummary {
  jira_account_id?: string | null;
  jira_display_name: string;
  hours: number;
  worklog_count: number;
}

export interface ProjectCostCalculationResponse {
  applied_jql: string;
  applied_worklog_start_date?: string | null;
  applied_worklog_end_date?: string | null;
  total_hours: number;
  total_own_hours: number;
  total_third_party_hours: number;
  total_own_cost_cents: number;
  total_third_party_cost_cents: number;
  total_cost_cents: number;
  total_revenue_cents: number;
  total_margin_cents: number;
  companies: ProjectCostCompanySummary[];
  collaborators: ProjectCostCollaboratorSummary[];
  issues: ProjectCostIssueSummary[];
  unmatched_worklogs: ProjectCostUnmatchedSummary[];
  warnings?: string[];
  export_scope_sold_hours?: number | null;
  export_scope_incomplete?: boolean;
  export_scope_sap_hours?: number | null;
  export_scope_nsap_hours?: number | null;
  /** Chaves dos tickets-pai que tiveram filho apontado no De/Até (recorte Compras-e). */
  export_scope_worklog_parent_keys?: string[] | null;
}

export interface ComprasEExportIssueRow {
  key: string;
  summary: string;
  status: string;
  issue_type: string;
  project_key: string;
  parent_key: string;
  parent_summary: string;
  pack: 'sap' | 'nsap' | string;
  esteira?: 'portal' | 'cmpe' | string;
  hours_in_period: number;
  worklog_count: number;
  jira_browse_url: string;
  own_cost_cents?: number;
  third_party_cost_cents?: number;
  cost_cents?: number;
  own_hours?: number;
  third_party_hours?: number;
  unmatched_hours?: number;
}

/** Autor de worklog sem colaborador em Custos de Projetos. */
export type ComprasEUnmatchedAuthor = {
  jira_account_id?: string | null;
  jira_display_name: string;
  hours: number;
  worklog_count: number;
};

export interface ComprasEExportHoursResponse {
  applied_jql: string;
  worklog_start_date: string;
  worklog_end_date: string;
  sap_hours: number;
  nsap_hours: number;
  total_hours: number;
  sold_hours?: number | null;
  sap_issue_count: number;
  nsap_issue_count: number;
  issue_count: number;
  worklog_count: number;
  sap_keys?: string[];
  nsap_keys?: string[];
  root_keys?: string[];
  issues?: ComprasEExportIssueRow[];
  incomplete: boolean;
  warnings: string[];
  subtask_count?: number;
  subtask_nao_iniciado_count?: number;
  subtask_tagged_count?: number;
  unmatched_hours?: number;
  unmatched_author_count?: number;
  unmatched_authors?: ComprasEUnmatchedAuthor[];
}

export interface ProjectsNlQueryJiraItem {
  key: string;
  summary: string;
  status?: string;
  project_key?: string;
  project_name?: string;
  issue_type?: string;
  updated?: string | null;
  jira_browse_url?: string;
}

export interface ProjectsNlQueryJiraResult {
  used_jql: string;
  total: number;
  items: ProjectsNlQueryJiraItem[];
}

export interface ProjectsNlQueryDatabaseResult {
  used_sql: string;
  columns: string[];
  rows: Array<Array<unknown>>;
}

export interface ProjectsNlQueryResponse {
  answer_text: string;
  jira?: ProjectsNlQueryJiraResult | null;
  database?: ProjectsNlQueryDatabaseResult | null;
  applied_guards: string[];
  warnings: string[];
}

export interface ProjectCostConfigExportCompanyItem {
  name: string;
  description?: string | null;
  default_hourly_rate?: number | null;
  is_active?: boolean;
}

export interface ProjectCostConfigExportRoleItem {
  name: string;
  description?: string | null;
  default_hourly_rate?: number | null;
  is_active?: boolean;
}

export interface ProjectCostConfigExportDemandRateItem {
  demand_type: string;
  label: string;
  hourly_rate: number;
  effective_start?: string | null;
  effective_end?: string | null;
  is_active?: boolean;
  notes?: string | null;
}

export interface ProjectCostConfigExportCompanyRoleRateItem {
  company_name: string;
  role_name: string;
  hourly_rate: number;
  effective_start?: string | null;
  effective_end?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export interface ProjectCostConfigExportCollaboratorItem {
  name: string;
  email?: string | null;
  jira_account_id?: string | null;
  jira_display_name?: string | null;
  user_email?: string | null;
  company_name: string;
  role_name?: string | null;
  hourly_rate_override?: number | null;
  is_third_party?: boolean;
  is_active?: boolean;
  notes?: string | null;
}

export interface ProjectCostConfigExportCollaboratorRateItem {
  collaborator_email?: string | null;
  collaborator_jira_account_id?: string | null;
  collaborator_name?: string | null;
  collaborator_company_name?: string | null;
  hourly_rate: number;
  effective_start?: string | null;
  effective_end?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export interface ProjectCostConfigExportPayload {
  schema_version: number;
  exported_at: string;
  companies: ProjectCostConfigExportCompanyItem[];
  roles: ProjectCostConfigExportRoleItem[];
  demand_rates: ProjectCostConfigExportDemandRateItem[];
  company_role_rates: ProjectCostConfigExportCompanyRoleRateItem[];
  collaborators: ProjectCostConfigExportCollaboratorItem[];
  collaborator_rates: ProjectCostConfigExportCollaboratorRateItem[];
}

export interface ProjectCostConfigImportPayload {
  schema_version: number;
  dry_run?: boolean;
  companies: ProjectCostConfigExportCompanyItem[];
  roles: ProjectCostConfigExportRoleItem[];
  demand_rates: ProjectCostConfigExportDemandRateItem[];
  company_role_rates: ProjectCostConfigExportCompanyRoleRateItem[];
  collaborators: ProjectCostConfigExportCollaboratorItem[];
  collaborator_rates: ProjectCostConfigExportCollaboratorRateItem[];
}

export interface ProjectCostConfigImportEntityResult {
  created: number;
  updated: number;
  skipped: number;
}

export interface ProjectCostConfigImportErrorItem {
  entity: string;
  identifier: string;
  message: string;
}

export interface ProjectCostConfigImportResponse {
  schema_version: number;
  dry_run: boolean;
  companies: ProjectCostConfigImportEntityResult;
  roles: ProjectCostConfigImportEntityResult;
  demand_rates: ProjectCostConfigImportEntityResult;
  company_role_rates: ProjectCostConfigImportEntityResult;
  collaborators: ProjectCostConfigImportEntityResult;
  collaborator_rates: ProjectCostConfigImportEntityResult;
  errors: ProjectCostConfigImportErrorItem[];
}

export interface ProjectCostManagementSnapshot {
  companies: ProjectCostCompany[];
  roles: ProjectCostRole[];
  company_role_rates: ProjectCostCompanyRoleRate[];
  collaborator_rates: ProjectCostCollaboratorRate[];
  collaborators: ProjectCostCollaborator[];
  demand_rates: ProjectCostDemandRate[];
}

/** Urgência informada pelo usuário ao abrir um chamado. Exatamente 3 valores válidos. */
export type TicketUrgency = 'ALTA' | 'MEDIA' | 'BAIXA';

export interface TicketTemplate {
  id: number;
  user_id: number;
  title: string;
  description: string;
  category?: string | null;
  urgency?: TicketUrgency | null;
  priority?: 'p1' | 'p2' | 'p3' | 'p4' | null;
  requester_id?: number | null;
  assigned_group_id?: number | null;
  workflow_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface Workflow {
  id: number;
  name: string;
  description?: string;
  steps?: string;
  is_active: boolean;
  version?: number;
  is_published?: boolean;
  parent_workflow_id?: number | null;
  area_type?: AreaType;
  created_at: string;
  groups?: Group[];
}

export interface WorkflowSubtaskType {
  id: number;
  workflow_id: number;
  name: string;
  description?: string;
  workflow_id_for_subtask?: number;
  default_priority: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Ticket {
  item_source?: 'ticket';
  id: number;
  title: string;
  description: string;
  status: 'novo' | 'aberto' | 'em_andamento' | 'aguardando_informacao_usuario' | 'aguardando_terceiros' | 'aguardando_evidencia' | 'aguardando_problema' | 'aguardando_implantacao' | 'resolvido' | 'cancelado' | 'fechado';
  waiting_reason?: string;
  /** Urgência informada pelo usuário — sempre presente. */
  urgency: TicketUrgency;
  /** Prioridade interna — omitida pelo backend para usuário comum (USUARIO). */
  priority?: 'p1' | 'p2' | 'p3' | 'p4';
  created_by: number;
  requester_id?: number;
  assigned_to?: number;
  assigned_group_id?: number;
  workflow_id?: number;
  current_step?: string;
  /** Status mapeado para a etapa atual no fluxo (API) */
  expected_status?: string | null;
  parent_ticket_id?: number;
  taken_by?: number;
  taken_at?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  closed_at?: string;
  creator?: User;
  requester?: User;
  assignee?: User;
  taken_by_user?: User;
  subtasks?: Ticket[];
}

export interface ActivityLog {
  id: number;
  ticket_id: number;
  user_id: number;
  activity_type: 'criado' | 'distribuido' | 'atribuido' | 'assumido' | 'redirecionado' | 'fluxo_alterado' | 'status_alterado' | 'informacao_alterada' | 'comentario' | 'anexo' | 'encerrado';
  description: string;
  old_value?: string;
  new_value?: string;
  group_id?: number;
  created_at: string;
  user?: User;
  group?: Group;
}

export interface WorkflowChangeGroupOption {
  id: number;
  name: string;
  member_count: number;
}

export interface WorkflowChangeWorkflowOption {
  id: number;
  name: string;
  groups: WorkflowChangeGroupOption[];
}

export interface WorkflowChangeOptionsResponse {
  current_workflow_id?: number;
  current_workflow_name?: string;
  current_group_id?: number;
  current_group_name?: string;
  workflows: WorkflowChangeWorkflowOption[];
}

export interface WorkflowChangeCustomFieldItem {
  custom_field_id: number;
  text_value?: string;
  file_path?: string;
  file_paths?: string;
}

export interface EntityWorkflowChangePayload {
  destination_workflow_id: number;
  destination_group_id: number;
  expected_updated_at: string;
  custom_field_values?: WorkflowChangeCustomFieldItem[];
  justification: string;
}

export interface PurchaseWorkflowChangeResponse {
  purchase_request: PurchaseRequest;
  origin_workflow_name?: string;
  destination_workflow_name: string;
  origin_group_name?: string;
  destination_group_name: string;
  responsible_removed: boolean;
  caller_retains_access: boolean;
}

export interface TicketTime {
  ticket_id: number;
  total_time_seconds: number;
  time_in_groups: Array<{
    group_id: number;
    group_name: string;
    assigned_at: string;
    unassigned_at?: string;
    time_spent_seconds: number;
    is_active: boolean;
  }>;
  time_waiting?: Array<{
    id: number;
    group_id?: number;
    group_name?: string;
    waiting_reason?: string;
    started_at: string;
    ended_at?: string;
    time_spent_seconds: number;
    is_active: boolean;
  }>;
  total_waiting_time_seconds?: number;
  current_group_time?: number;
  time_in_steps?: Array<{
    step_name: string;
    started_at: string;
    ended_at?: string;
    time_spent_seconds: number;
    is_active: boolean;
  }>;
}

export interface TicketFinalReportCustomField {
  custom_field_id: number;
  name: string;
  label: string;
  field_type: string;
  value?: string | null;
}

export interface TicketFinalReportStepTime {
  step_name: string;
  time_spent_seconds: number;
  time_spent_label: string;
}

export interface TicketFinalReportDeadlineEvaluation {
  total_flow_time_seconds: number;
  total_flow_time_label: string;
  step_time_breakdown_label: string;
  step_times: TicketFinalReportStepTime[];
  status: string;
  current_step?: string | null;
}

export interface TicketFinalReport {
  ticket_id: number;
  title: string;
  workflow_name?: string | null;
  deadline_evaluation: TicketFinalReportDeadlineEvaluation;
  custom_fields: TicketFinalReportCustomField[];
  generated_at: string;
}

export interface Comment {
  id: number;
  ticket_id: number;
  user_id: number;
  content: string;
  is_internal: boolean;
  created_at: string;
  user?: User;
}

export interface Attachment {
  id: number;
  ticket_id: number;
  filename: string;
  file_path: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_size?: number;
  mime_type?: string;
  description?: string;
  uploaded_by: number;
  uploaded_at: string;
}

/** Ocorrencia de anexo sem referencia S3 completa (admin). */
export interface TicketAttachmentWithoutS3Occurrence {
  kind: 'ticket_attachment' | 'ticket_cfv_file' | 'ticket_cfv_files_item';
  attachment_id?: number | null;
  custom_field_value_id?: number | null;
  custom_field_id?: number | null;
  file_index?: number | null;
  filename: string;
  path: string;
  reason: string;
}

export interface TicketWithoutS3AttachmentsItem {
  ticket_id: number;
  title: string;
  status: string;
  created_by_name?: string | null;
  taken_by_name?: string | null;
  updated_at: string;
  occurrence_count: number;
  occurrences: TicketAttachmentWithoutS3Occurrence[];
}

export interface TicketsWithoutS3AttachmentsResponse {
  items: TicketWithoutS3AttachmentsItem[];
  total_tickets: number;
  skip: number;
  limit: number;
}

export interface KnowledgeArticleMissingImageOccurrence {
  source: 'article_body_img' | string;
  image_url: string;
  image_name?: string | null;
  reason: string;
}

export interface KnowledgeArticleWithMissingImagesItem {
  article_id: number;
  title: string;
  category_name?: string | null;
  updated_at: string;
  occurrence_count: number;
  occurrences: KnowledgeArticleMissingImageOccurrence[];
}

export interface KnowledgeArticlesWithMissingImagesResponse {
  items: KnowledgeArticleWithMissingImagesItem[];
  total_articles: number;
  skip: number;
  limit: number;
}

export interface WorkflowCustomField {
  id: number;
  workflow_id: number;
  name: string;
  label: string;
  description?: string;  // Descrição do campo exibida abaixo do título
  field_type: 'text_short' | 'text_long' | 'list' | 'combo' | 'file' | 'files' | 'date' | 'period' | 'link' | 'user' | 'analyst' | 'group' | 'integer' | 'decimal' | 'currency' | 'calculation' | 'check' | 'approver' | 'contract_manager_approver' | 'platform_document' | 'technical_proposal_review' | 'supplier_selection' | 'table';
  options?: string;
  approver_questions?: string;  // JSON com perguntas adicionais ao aprovador
  allow_multiple: boolean;
  is_required: boolean;
  order: number;
  group_name?: string;  // Nome do agrupamento (tab)
  conditional_field_id?: number;  // ID do campo que controla a visibilidade
  conditional_operator?: 'equals' | 'greater_than' | 'greater_equal' | 'less_than' | 'less_equal' | 'not_equals' | 'contains' | 'between';  // Operador de comparação
  conditional_value?: string;  // Valor(s) que ativam a exibição (JSON array ou string)
  validation_field_id?: number;  // ID do campo usado para validação
  validation_rules?: string;  // Regras de validação em JSON
  checkbox_label?: string;  // Texto personalizado ao lado da caixa de marcação
  checkbox_text?: string;  // Texto a ser exibido quando checkbox é marcado
  checkbox_text_trigger_option?: string;  // Opção específica que deve estar marcada para exibir o texto (apenas quando allow_multiple é True)
  checkbox_link?: string;  // Link a ser exibido no campo checkbox
  checkbox_link_text?: string;  // Texto personalizado do botão do link
  document_type_id?: number;  // Tipo de documento específico para campos platform_document
  created_at: string;
  updated_at: string;
  step_is_required?: boolean;  // Se o campo é obrigatório na etapa atual
  step_is_visible?: boolean;  // Se o campo é visível na etapa atual
  step_is_blocked?: boolean;  // Se o campo está bloqueado (readonly) na etapa atual
}

export interface TicketCustomFieldValue {
  id: number;
  ticket_id: number;
  custom_field_id: number;
  text_value?: string;
  file_path?: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_paths?: string;
  created_at: string;
  updated_at: string;
  custom_field?: WorkflowCustomField;
}

export interface Holiday {
  id: number;
  date: string;
  name: string;
  created_by: number;
  created_at: string;
  creator?: {
    id: number;
    name: string;
    email: string;
    role: string;
    is_active: boolean;
    preferred_theme?: string;
    created_at: string;
  };
}

export interface KnowledgeArticleCategory {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeCategoryExportItem {
  name: string;
  description?: string;
}

export interface KnowledgeCategoriesExportPayload {
  version: string;
  exported_at: string;
  categories: KnowledgeCategoryExportItem[];
}

export interface KnowledgeCategoryImportItem {
  name: string;
  description?: string;
}

export interface KnowledgeCategoriesImportPayload {
  version?: string;
  categories: KnowledgeCategoryImportItem[];
}

export interface KnowledgeCategoryImportResultItem {
  name: string;
  action: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
}

export interface KnowledgeCategoriesImportResponse {
  success: boolean;
  dry_run: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  results: KnowledgeCategoryImportResultItem[];
}

export interface KnowledgeArticleEditorEntry {
  user_id: number;
  name: string;
  email: string;
  granted_at: string;
}

export type KnowledgeGlobalKbEditorEntry = KnowledgeArticleEditorEntry;

export interface KnowledgeArticle {
  id: number;
  title: string;
  body: string;
  category_id: number;
  created_by: number;
  author_name?: string;
  created_at: string;
  updated_at: string;
  category?: KnowledgeArticleCategory;
  author?: User;
  is_favorite?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  article_editors?: KnowledgeArticleEditorEntry[] | null;
}

export interface KnowledgeAuthorCorrelationItem {
  article_id: number;
  title: string;
  author_name: string;
  current_created_by: number;
  matched_user_id?: number | null;
  matched_user_name?: string | null;
  score?: number | null;
  second_score?: number | null;
  status: string;
}

export interface KnowledgeAuthorCorrelationResponse {
  apply: boolean;
  articles_examined: number;
  high_confidence: KnowledgeAuthorCorrelationItem[];
  needs_review: KnowledgeAuthorCorrelationItem[];
  not_found: KnowledgeAuthorCorrelationItem[];
  updated_article_ids: number[];
  updated_count: number;
}

export interface KnowledgeArticleBatchImportErrorItem {
  row: number;
  title?: string;
  message: string;
}

export interface KnowledgeArticleBatchImportResponse {
  success: boolean;
  total_rows: number;
  created: number;
  errors: number;
  created_categories: number;
  error_details: KnowledgeArticleBatchImportErrorItem[];
}

export interface KnowledgeBaseReferenceItem {
  article_id: number;
  title: string;
  path: string;
}

export interface KnowledgeBaseAskResponse {
  answer: string;
  references: KnowledgeBaseReferenceItem[];
}

export interface KnowledgeBaseAssistantVisibilityResponse {
  assistant_visible_for_general: boolean;
}

export interface KnowledgeArticleFavorite {
  id: number;
  article_id: number;
  user_id: number;
  created_at: string;
  article?: KnowledgeArticle;
}

export interface PurchaseRequest {
  item_source?: 'purchase_subtask';
  id: number;
  public_id_base?: number;
  public_subtask_seq?: number;
  public_id?: string;
  parent_public_id?: string;
  title: string;
  object: string;
  items_description: string;
  description: string;
  status: 'novo' | 'aberto' | 'em_andamento' | 'aguardando_informacao_usuario' | 'aguardando_terceiros' | 'aguardando_evidencia' | 'aguardando_problema' | 'aguardando_implantacao' | 'aguardando_aprovacao' | 'aguardando_reenvio' | 'resolvido' | 'cancelado' | 'fechado' | 'rascunho';
  waiting_reason?: string;
  /** Urgência informada pelo usuário — sempre presente. */
  urgency: TicketUrgency;
  /** Prioridade interna — omitida pelo backend para usuário comum (USUARIO). */
  priority?: 'p1' | 'p2' | 'p3' | 'p4';
  created_by: number;
  requester_id?: number;
  assigned_to?: number;
  assigned_group_id?: number;
  workflow_id?: number;
  current_step?: string;
  requester_review_return_step?: string;
  parent_request_id?: number;
  /** Legado/alternativo — preferir parent_request_id (nome retornado pela API) */
  parent_purchase_request_id?: number;
  taken_by?: number;
  taken_at?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  closed_at?: string;
  creator?: User;
  requester?: User;
  assignee?: User;
  taken_by_user?: User;
  subtasks?: PurchaseRequest[];
  approver_id?: number;
  approvals?: PurchaseApproval[];
  /** Subtarefa direcionada a uma pessoa, sem fila de grupo. */
  is_individual_subtask?: boolean;
  /** Calculado pela ACL do pai no backend; ausente quando o item não é subtarefa. */
  can_access_parent?: boolean;
}

/** Destino de uma subtarefa: fila de grupo ou responsável individual. */
export type PurchaseSubtaskTargetType = 'group' | 'user';

export interface PurchaseApproval {
  id: number;
  purchase_request_id: number;
  approver_id: number;
  status: 'pendente' | 'aprovado' | 'reprovado';
  approval_type_label?: string;
  approval_context_label?: string;
  justification?: string;
  contract_manager_source_field_id?: number;
  approver_questions?: ApproverQuestion[];
  question_answers?: ApproverQuestionAnswerRecord[];
  created_at: string;
  updated_at: string;
  approver?: User;
}

export interface PurchaseActivityLog {
  id: number;
  purchase_request_id: number;
  user_id: number;
  activity_type: 'criado' | 'distribuido' | 'atribuido' | 'assumido' | 'status_alterado' | 'comentario' | 'anexo' | 'encerrado' | 'aprovado' | 'reprovado';
  description: string;
  old_value?: string;
  new_value?: string;
  group_id?: number;
  created_at: string;
  user?: User;
  group?: Group;
}

export interface PurchaseComment {
  id: number;
  purchase_request_id: number;
  user_id: number;
  content: string;
  is_internal: boolean;
  created_at: string;
  user?: User;
}

export interface PurchaseAttachment {
  id: number;
  purchase_request_id: number;
  filename: string;
  file_path: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_size?: number;
  mime_type?: string;
  description?: string;
  visible_to_requester?: boolean;
  uploaded_by: number;
  uploaded_at: string;
  /** Anexos sintéticos (campos customizados) — usar com kind purchase_cfv no reparo */
  custom_field_value_id?: number | null;
  custom_field_file_index?: number | null;
}

export interface PurchaseCustomFieldValue {
  id: number;
  purchase_request_id: number;
  custom_field_id: number;
  text_value?: string;
  file_path?: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_paths?: string;
  document_instance_id?: number;
  created_at: string;
  updated_at: string;
  custom_field?: WorkflowCustomField;
  document_instance?: DocumentInstance;
}

export interface Opportunity {
  id: number;
  title: string;
  description?: string;
  status: string;
  purchase_request_id?: number | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: User;
  purchase_request?: {
    id: number;
    public_id?: string | null;
    title: string;
    status: string;
    current_step?: string | null;
  } | null;
}

export interface OpportunityLinkablePurchase {
  id: number;
  public_id?: string | null;
  title: string;
  status: string;
  current_step?: string | null;
  created_at: string;
}

export interface OpportunityModuleCustomFieldValue {
  id: number;
  custom_field_id: number;
  text_value?: string;
  file_path?: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_paths?: string;
  document_instance_id?: number;
  created_at: string;
  updated_at: string;
  custom_field?: WorkflowCustomField;
  document_instance?: DocumentInstance;
}

export interface OpportunityFolderTreeNode {
  id: number;
  name: string;
  parent_folder_id?: number | null;
  children: OpportunityFolderTreeNode[];
}

export interface OpportunityDocument {
  id: number;
  opportunity_id: number;
  folder_id?: number | null;
  filename: string;
  file_path: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_size?: number | null;
  mime_type?: string | null;
  uploaded_by: number;
  uploaded_at: string;
  updated_at: string;
  folder_path?: string;
  uploader?: User;
}

export interface OpportunityFolderDeletePreview {
  folder_id: number;
  folder_name: string;
  total_folders: number;
  total_documents: number;
  folders: string[];
  documents: string[];
  has_more_folders: boolean;
  has_more_documents: boolean;
}

export interface OpportunityDocumentListResponse {
  items: OpportunityDocument[];
  total: number;
  skip: number;
  limit: number;
}

// Document Types
export interface DocumentType {
  id: number;
  name: string;
  description?: string;
  area_type: AreaType;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: User;
  template_fields?: DocumentTemplateField[];
}

export interface DocumentTemplateField {
  id: number;
  document_type_id: number;
  name: string;
  label: string;
  description?: string;
  field_type: 'text_short' | 'text_long' | 'list' | 'combo' | 'file' | 'files' | 'date' | 'period' | 'link' | 'user' | 'analyst' | 'group' | 'integer' | 'decimal' | 'currency' | 'calculation' | 'check' | 'instruction_text' | 'score';
  options?: string;
  allow_multiple: boolean;
  is_required: boolean;
  order: number;
  group_name?: string;
  conditional_field_id?: number;
  conditional_operator?: 'equals' | 'greater_than' | 'greater_equal' | 'less_than' | 'less_equal' | 'not_equals' | 'contains' | 'between';
  conditional_value?: string;
  validation_field_id?: number;
  validation_rules?: string;
  checkbox_label?: string;
  checkbox_text?: string;
  checkbox_text_trigger_option?: string;
  checkbox_link?: string;
  checkbox_link_text?: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentInstance {
  id: number;
  document_type_id: number;
  title?: string;
  created_by: number;
  status: 'draft' | 'finalized';
  created_at: string;
  updated_at: string;
  finalized_at?: string;
  revision?: number;
  opportunity?: string;
  document_type?: DocumentType;
  creator?: User;
  answers?: DocumentAnswer[];
}

export interface DocumentAnswer {
  id: number;
  document_instance_id: number;
  template_field_id: number;
  text_value?: string;
  file_path?: string;
  storage_provider?: string;
  s3_object_key?: string | null;
  file_missing?: boolean;
  file_paths?: string;
  created_at: string;
  updated_at: string;
  template_field?: DocumentTemplateField;
}

// Document Types Import/Export
export interface DocumentTemplateFieldExport {
  name: string;
  label: string;
  description?: string;
  field_type: string;
  options?: string;
  allow_multiple: boolean;
  is_required: boolean;
  order: number;
  group_name?: string;
  conditional_field_name?: string;
  conditional_operator?: string;
  conditional_value?: string;
  validation_field_name?: string;
  validation_rules?: string;
  checkbox_label?: string;
  checkbox_text?: string;
  checkbox_text_trigger_option?: string;
  checkbox_link?: string;
  checkbox_link_text?: string;
}

export interface DocumentTypeExport {
  name: string;
  description?: string;
  area_type: AreaType;
  is_active: boolean;
  template_fields: DocumentTemplateFieldExport[];
}

export interface DocumentTypesExportPayload {
  version: string;
  exported_at: string;
  area_type: AreaType;
  document_types: DocumentTypeExport[];
}

export interface DocumentTypeImportResult {
  name: string;
  action: 'created' | 'updated';
  fields_created: number;
  fields_updated: number;
  fields_deleted: number;
  errors: string[];
}

export interface DocumentTypesImportResponse {
  success: boolean;
  total_types: number;
  types_created: number;
  types_updated: number;
  results: DocumentTypeImportResult[];
  errors: string[];
}

export interface PurchaseTime {
  purchase_request_id: number;
  total_time_seconds: number;
  time_in_groups: Array<{
    group_id: number;
    group_name: string;
    assigned_at: string;
    unassigned_at?: string;
    time_spent_seconds: number;
    is_active: boolean;
  }>;
  time_waiting?: Array<{
    id: number;
    group_id?: number;
    group_name?: string;
    waiting_reason?: string;
    started_at: string;
    ended_at?: string;
    time_spent_seconds: number;
    is_active: boolean;
  }>;
  total_waiting_time_seconds?: number;
  current_group_time?: number;
  time_in_steps?: Array<{
    step_name: string;
    started_at: string;
    ended_at?: string;
    time_spent_seconds: number;
    is_active: boolean;
  }>;
}

export interface PurchaseFinalReportCustomField {
  custom_field_id: number;
  name: string;
  label: string;
  field_type: string;
  value?: string | null;
}

export interface PurchaseFinalReportStepTime {
  step_name: string;
  time_spent_seconds: number;
  time_spent_label: string;
}

export interface PurchaseFinalReportDeadlineEvaluation {
  total_flow_time_seconds: number;
  total_flow_time_label: string;
  step_time_breakdown_label: string;
  step_times: PurchaseFinalReportStepTime[];
  status: string;
  current_step?: string | null;
}

export interface PurchaseFinalReport {
  purchase_request_id: number;
  purchase_public_id?: string | null;
  title: string;
  workflow_name?: string | null;
  assigned_analyst_name?: string | null;
  contracting_type?: string | null;
  saving_amount?: string | null;
  deadline_evaluation: PurchaseFinalReportDeadlineEvaluation;
  custom_fields: PurchaseFinalReportCustomField[];
  generated_at: string;
}

export interface AnalyticsBucket {
  key: string;
  label: string;
  value: number;
}

export interface AnalyticsTimeBucket {
  date: string;
  count: number;
}

export interface AnalyticsStepTime {
  step_name: string;
  avg_time_seconds: number;
  total_records: number;
}

export interface AnalyticsComparison {
  current: number;
  previous: number;
  delta: number;
  delta_percentage: number;
}

export interface AnalyticsDashboardKpis {
  total_items: number;
  avg_total_time_seconds: number;
  sla_within_count: number;
  sla_outside_count: number;
  sla_within_percentage: number;
  backlog_open_count: number;
}

export interface AnalyticsCustomFieldOption {
  id: number;
  name: string;
  label: string;
  field_type: string;
  workflow_id: number;
  workflow_name?: string;
}

export interface AnalyticsDashboardResponse {
  area_type: AreaType;
  date_from: string;
  date_to: string;
  comparison_date_from: string;
  comparison_date_to: string;
  applied_filters: Record<string, unknown>;
  kpis: AnalyticsDashboardKpis;
  comparison: Record<string, AnalyticsComparison>;
  volume_by_period: AnalyticsTimeBucket[];
  previous_volume_by_period: AnalyticsTimeBucket[];
  by_status: AnalyticsBucket[];
  by_priority: AnalyticsBucket[];
  by_group: AnalyticsBucket[];
  by_workflow: AnalyticsBucket[];
  by_category: AnalyticsBucket[];
  by_custom_field: AnalyticsBucket[];
  step_time_by_stage: AnalyticsStepTime[];
  available_custom_fields: AnalyticsCustomFieldOption[];
  grouping: string;
  drilldown_base_path: string;
}

export interface DashboardView {
  id: number;
  user_id: number;
  name: string;
  area_type: AreaType;
  filters_json: Record<string, unknown>;
  layout_json: Record<string, unknown>;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface DashboardViewPayload {
  name: string;
  area_type: AreaType;
  filters_json: Record<string, unknown>;
  layout_json: Record<string, unknown>;
  is_default: boolean;
}

// ============================================================
// Feedback module (mirrors backend/app/services/feedback)
// ============================================================

export type FeedbackCadence =
  | 'DAILY'
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'ANNUALLY';

export interface FeedbackWorkflowOption {
  id: number;
  name: string;
  version: number;
  area_type: AreaType;
  is_active: boolean;
}

export type FeedbackModuleId = 'platform' | 'tickets' | 'purchases' | 'knowledge';

export type FeedbackSubjectType = 'menu' | 'workflow';

export interface FeedbackMenuTargetOption {
  key: string;
  label: string;
  group?: string | null;
  sort_order: number;
}

export interface FeedbackModuleTargets {
  menus: FeedbackMenuTargetOption[];
  workflows: FeedbackWorkflowOption[];
}

export interface FeedbackModuleOption {
  id: FeedbackModuleId;
  label: string;
}

export interface FeedbackWorkflowPrefill {
  id: number;
  module: FeedbackModuleId;
  name: string;
  version: number;
}

export interface FeedbackTargetsResponse {
  modules: FeedbackModuleOption[];
  targets_by_module: Partial<Record<FeedbackModuleId, FeedbackModuleTargets>>;
  prefill_workflow?: FeedbackWorkflowPrefill | null;
}

export interface FeedbackAdminScopeOption {
  value: string;
  label: string;
  optgroup: string | null;
}

export interface FeedbackAdminScopesResponse {
  options: FeedbackAdminScopeOption[];
}

export interface FeedbackPayload {
  rating: number; // 1..5
  comment?: string | null;
  workflow_id?: number | null;
  feedback_module?: FeedbackModuleId | null;
  feedback_subject_type?: FeedbackSubjectType | null;
  feedback_subject_key?: string | null;
  is_anonymous?: boolean;
}

export interface Feedback {
  id: number;
  rating: number;
  comment: string | null;
  workflow_id: number | null;
  feedback_module?: FeedbackModuleId | null;
  feedback_subject_type?: FeedbackSubjectType | null;
  feedback_subject_key?: string | null;
  display_label?: string | null;
  is_anonymous: boolean;
  created_at: string;
  updated_at: string;
  workflow: FeedbackWorkflowOption | null;
  user: User | null;
}

export interface FeedbackListResponse {
  items: Feedback[];
  total: number;
  skip: number;
  limit: number;
}

export interface ShouldPromptResponse {
  should_prompt: boolean;
  cadence: FeedbackCadence;
  last_feedback_at: string | null;
  next_eligible_at: string | null;
}

export interface FeedbackPromptConfig {
  id: number;
  workflow_id: number | null;
  cadence: FeedbackCadence;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FeedbackPromptConfigPayload {
  workflow_id?: number | null;
  cadence: FeedbackCadence;
  is_active?: boolean;
}

// Limite de caracteres do comentário (espelha o backend)
export const FEEDBACK_COMMENT_MAX_LENGTH = 250;

export interface FeedbackRatingDistribution {
  rating_1: number;
  rating_2: number;
  rating_3: number;
  rating_4: number;
  rating_5: number;
}

export interface FeedbackTopWorkflowItem {
  workflow_id: number | null;
  workflow_name: string | null;
  total: number;
  average_rating: number;
}

export interface FeedbackStats {
  total: number;
  average_rating: number;
  distribution: FeedbackRatingDistribution;
  top_workflows: FeedbackTopWorkflowItem[];
}

export interface FeedbackListFilters {
  workflow_id?: number | null;
  min_rating?: number;
  max_rating?: number;
  date_from?: string;
  date_to?: string;
  is_anonymous?: boolean;
  skip?: number;
  limit?: number;
}

export interface ServiceNowLegacyStatusResponse {
  configured: boolean;
  password_configured?: boolean;
  connected: boolean;
  openai_configured: boolean;
  openai_quota_exhausted?: boolean;
  table_count?: number | null;
  max_rows: number;
}

export interface ServiceNowLegacyAttachmentItem {
  sys_id: string;
  file_name: string;
  content_type: string;
  size_bytes?: number | null;
  table_name?: string | null;
  sys_created_on?: string | null;
  record_number?: string | null;
}

export interface ServiceNowLegacyAskResponse {
  answer: string;
  answer_mode?: 'openai' | 'structured';
  row_count: number;
  truncated: boolean;
  execution_ms: number;
  columns?: string[];
  rows?: Array<Record<string, unknown>>;
  sql_executed?: string;
  attachments?: ServiceNowLegacyAttachmentItem[];
}

export interface ServiceNowLegacySqlRecordHint {
  number: string;
  table: string;
  message: string;
  suggested_sql?: string | null;
}

export interface ServiceNowLegacySqlResponse {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  row_count: number;
  truncated: boolean;
  execution_ms: number;
  sql_executed: string;
  attachments?: ServiceNowLegacyAttachmentItem[];
  record_hint?: ServiceNowLegacySqlRecordHint | null;
}

export interface ServiceNowLegacyRecordRef {
  table: string;
  sys_id: string;
  number: string;
  short_description?: string | null;
}

export interface ServiceNowLegacyAttachmentsResponse {
  record: ServiceNowLegacyRecordRef;
  attachments: ServiceNowLegacyAttachmentItem[];
  total: number;
}
